
// Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

// Third party
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, requiredIf } from "vuelidate/lib/validators";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vselect3";

// Types
import { Domain, DomainGroup } from "@/store/user-management/types";
import { UserPermissions, UserGroupTable } from "@/store/types";

// Enums
import { ModalMode } from "@/store/user-management/enums";

// Default Domain Group
import { domainGroupTemplate } from "./DomainGroupMappingTable.vue";

// Namespaces
const UserManagement = namespace("userManagement");

@Component({
    mixins: [validationMixin],
    validations: {
        currentDomainGroup: {
            domainGroupName: {
                required,
                minLength: minLength(1),
                maxLength: maxLength(255)
            },
            userGroupId: {
                required,
                minLength: minLength(1) // Unselected is 0
            }
        },
        hasDataChanged: {
            required: requiredIf(function() {
                return (
                    this.selectedDomainGroup.userGroupId === this.currentDomainGroup.userGroupId &&
                    this.selectedDomainGroup.domainGroupName === this.currentDomainGroup.domainGroupName
                );
            })
        }
    },
    components: {
        "v-select": vSelect,
        VuePerfectScrollbar: VuePerfectScrollbar
    }
})

export default class DomainGroupMappingModal extends Vue {
    // Props
    @Prop({ default: null, type: Object }) selectedDomainGroup: DomainGroup;
    @Prop({ default: [], type: Array }) selectableUserGroups: UserGroupTable[];
    @Prop({ default: false, type: Boolean }) visible: boolean;
    @Prop({ default: null, type: Number }) currentMode: ModalMode;

    // Getters
    @Getter getPermissions: UserPermissions;
    @UserManagement.Getter private getDomain: (domainId: number) => Domain;

    // Actions
    @UserManagement.Action createDomainGroup: (domainGroup: DomainGroup) => Promise<void>;
    @UserManagement.Action updateDomainGroup: ({ domainId, userGroupId, domainGroup }: { domainId: number; userGroupId: number; domainGroup: DomainGroup; }) => Promise<void>;

    private modes: typeof ModalMode = ModalMode;
    private currentDomainGroup: DomainGroup = { ...domainGroupTemplate };

    @Emit("toggle-modal")
    private closeModal(): void {
        // Reset data back to default state
        this.currentDomainGroup = { ...domainGroupTemplate };
    }

    @Watch("visible")
    private modalToggle(toggle: boolean): void {
        // Keep selectedDomainGroup the original state and use currentDomainGroup for data manipulation
        if (toggle) {
            this.currentDomainGroup = { ...this.selectedDomainGroup };
        } else {
            this.currentDomainGroup = { ...domainGroupTemplate };
        }
    }

    // Modal is set to read only when mode is view
    private get readOnly(): boolean {
        return this.currentMode === this.modes.View;
    }

    // If permission is on / off
    private get isDomainGroupMappingEnabled(): boolean {
        return this.getPermissions.canEditDomainGroups || this.getPermissions.canViewDomainGroups;
    }

    // Gets the domain using the select domainGroup and returns the domain's name
    private get domainName(): string {
        if (this.selectedDomainGroup == null || this.selectedDomainGroup.domainId == 0) {
            return "";
        }

        return this.getDomain(this.selectedDomainGroup.domainId).domainName;
    }

    // Updates the current domain group values upon changing the user group from the drop down
    private userGroupChange(userGroup: UserGroupTable): void {
        if (userGroup) {
            this.currentDomainGroup.userGroupId = userGroup.userGroupId;
            this.currentDomainGroup.userGroupTitle = userGroup.title;
        }
    }

    // This function handles the operations needed to be carried out against the current domain group
    private async saveDomainGroup(): Promise<void> {
        if (this.currentMode === this.modes.Create) {
            await this.createDomainGroup(this.currentDomainGroup);
        } else if (this.currentMode === this.modes.Edit) {
            await this.updateDomainGroup({ 
                domainId: this.selectedDomainGroup.domainId,
                userGroupId: this.selectedDomainGroup.userGroupId,
                domainGroup: this.currentDomainGroup 
            });
        }
        this.closeModal();
    }
}
